import { Inject, Injectable, Pipe, PipeTransform } from '@angular/core';
import moment from 'moment';
import { UNIFIED_PATIENTS_CONFIG, type UnifiedPatientsConfig } from './unified-patients-config.token';

@Injectable({ providedIn: 'root' })
export class DobFormatService {
	constructor(@Inject(UNIFIED_PATIENTS_CONFIG) private readonly config: UnifiedPatientsConfig) {}

	format(dateOfBirth: string): string {
		if (!dateOfBirth) {
			return '';
		}

		const fmt = this.getEffectiveFormat();
		const parsed = moment(dateOfBirth);

		return parsed.isValid() ? parsed.format(fmt) : dateOfBirth;
	}

	/**
	 * Parse a display-formatted date string back to ISO (YYYY-MM-DD).
	 * Returns '' if empty, '0001-01-01' if invalid/incomplete, or the valid ISO date.
	 */
	parse(displayValue: string): string {
		if (!displayValue) {
			return '';
		}

		const fmt = this.getEffectiveFormat();
		const parsed = moment(displayValue, fmt, true);

		return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '0001-01-01';
	}

	/**
	 * Compares two date-of-birth strings for calendar-day equality.
	 * Tries ISO formats first (canonical), then the configured display format as fallback.
	 * Returns false if either string is empty or unparseable.
	 */
	isSameDoB(a: string, b: string): boolean {
		if (!a || !b) {
			return false;
		}

		const formats = ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm:ss.SSSZ', this.getEffectiveFormat()];
		const da = moment(a, formats, true).isValid() ? moment(a, formats, true) : moment(a, moment.ISO_8601, true);
		const db = moment(b, formats, true).isValid() ? moment(b, formats, true) : moment(b, moment.ISO_8601, true);

		if (!da.isValid() || !db.isValid()) {
			return false;
		}

		return da.isSame(db, 'day');
	}

	getEffectiveFormat(): string {
		const dateFormat = this.config.getDateFormat();
		const dobMask = this.config.getDobMask();

		if (!dobMask) {
			return dateFormat.toUpperCase();
		}

		let result = dateFormat;

		[/yyyy/i, /mm/i, /dd/i].forEach(part => {
			if (!dobMask.match(part)) {
				result = result.replace(part, '');
			}
		});

		result = result.replace(/([^a-z]+)(?=[^a-z])/gi, '');

		return result.replace(/^[^a-z]+|[^a-z]+$/gi, '').toUpperCase();
	}

	/**
	 * Applies the configured date mask to raw user input.
	 * Strips non-digit characters, inserts format separators, and truncates to max length.
	 */
	applyMask(value: string): string {
		const format = this.getEffectiveFormat();
		const maxDigits = format.replace(/[^A-Za-z]/g, '').length;
		const separators = [...format].map((ch, i) => (/[^A-Za-z]/.test(ch) ? { i, ch } : null)).filter(Boolean) as {
			i: number;
			ch: string;
		}[];
		const digits = value.replace(/[^0-9]/g, '').slice(0, maxDigits);
		let result = '';
		let di = 0;

		for (let i = 0; i < format.length && di < digits.length; i++) {
			const sep = separators.find(s => s.i === i);

			result += sep ? sep.ch : digits[di++];
		}

		return result;
	}

	/**
	 * Returns true if the given ISO date string represents an invalid DOB
	 * (unparseable, before 1900, or in the future).
	 */
	isInvalidDob(isoDate: string): boolean {
		if (!isoDate) {
			return false;
		}

		const date = new Date(isoDate);

		return isNaN(date.getTime()) || date < new Date('1900-01-01') || date > new Date();
	}
}

@Pipe({ name: 'dobFormat', standalone: true })
export class DobFormatPipe implements PipeTransform {
	constructor(private readonly dobFormatService: DobFormatService) {}

	transform(dateOfBirth: string): string {
		return this.dobFormatService.format(dateOfBirth);
	}
}
